import { API_BASE, parseJsonOrThrow } from "./base";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(
  idToken: string,
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${idToken}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let message = text || res.statusText;
    if (text) {
      try {
        const parsed = JSON.parse(text) as { error?: string };
        message = parsed.error ?? message;
      } catch {
        // Keep the raw text body when the server did not return JSON.
      }
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as T;
}

export function apiClient(idToken: string) {
  return {
    requestUploadUrl: (body: {
      patientRef: string;
      fileName: string;
      contentType: string;
      orderTypeId?: string;
      organizationId?: string;
      recipientOrganizationNpi?: string;
      sourceMode?: import("../types").SourceMode;
    }) =>
      request<{ documentId: string; uploadUrl: string }>(
        idToken,
        "POST",
        "/documents/upload-url",
        body,
      ),

    getQueue: (status?: import("../types").DocumentStatus | "all") =>
      request<{ documents: import("../types").DocumentRow[] }>(
        idToken,
        "GET",
        `/documents/queue${status && status !== "all" ? `?status=${encodeURIComponent(status)}` : ""}`,
      ),

    getDocument: (documentId: string) =>
      request<{ document: import("../types").DocumentRow }>(
        idToken,
        "GET",
        `/documents/${documentId}`,
      ),

    getDownloadUrl: (documentId: string) =>
      request<{ downloadUrl: string; status: string; contentType: string }>(
        idToken,
        "GET",
        `/documents/${documentId}/download-url`,
      ),

    /** Runs OCR + the classification/compliance agent against the
     * uploaded document and persists the result (spec section 7). Safe
     * to call more than once before signing. */
    extract: (documentId: string) =>
      request<{ document: import("../types").DocumentRow }>(
        idToken,
        "POST",
        `/documents/${documentId}/extract`,
      ),

    /** `acknowledgeWarnings` is required when the document's
     * compliance_status is "exception" — see spec section 12. */
    sign: (documentId: string, acknowledgeWarnings?: boolean) =>
      request<{
        document: import("../types").DocumentRow;
        auditEvent: import("../types").AuditLogRow;
        version: import("../types").DocumentVersionRow;
      }>(
        idToken,
        "POST",
        `/documents/${documentId}/sign`,
        acknowledgeWarnings ? { acknowledgeWarnings: true } : undefined,
      ),

    requestFix: (documentId: string, note: string) =>
      request<{
        document: import("../types").DocumentRow;
        fixNote: import("../types").DocumentFixNoteRow;
      }>(idToken, "POST", `/documents/${documentId}/fix-request`, { note }),

    reject: (documentId: string, reason: string) =>
      request<{ document: import("../types").DocumentRow }>(
        idToken,
        "POST",
        `/documents/${documentId}/reject`,
        { reason },
      ),

    requestReuploadUrl: (
      documentId: string,
      body: { fileName: string; contentType: string; sourceMode?: import("../types").SourceMode },
    ) =>
      request<{ documentId: string; uploadUrl: string }>(
        idToken,
        "POST",
        `/documents/${documentId}/reupload-url`,
        body,
      ),

    completeUpload: (documentId: string) =>
      request<{ documentId: string; status: import("../types").DocumentStatus }>(
        idToken,
        "POST",
        `/documents/${documentId}/upload-complete`,
      ),

    getAuditTrail: (documentId: string) =>
      request<{ events: import("../types").AuditLogRow[] }>(
        idToken,
        "GET",
        `/documents/${documentId}/audit-trail`,
      ),

    listFixNotes: (documentId: string) =>
      request<{ fixNotes: import("../types").DocumentFixNoteRow[] }>(
        idToken,
        "GET",
        `/documents/${documentId}/fix-notes`,
      ),

    verifySeal: (documentId: string) =>
      request<{
        valid: boolean;
        sealedPdfHashMatches: boolean;
        sealSignatureValid: boolean;
        expectedSealedSha256Hex: string | null;
        actualSealedSha256Hex: string | null;
      }>(idToken, "GET", `/documents/${documentId}/seal-verification`),

    listOrderTypes: () =>
      request<{ orderTypes: import("../types").OrderTypeRow[] }>(idToken, "GET", "/order-types"),

    listVersions: (documentId: string) =>
      request<{ versions: import("../types").DocumentVersionRow[] }>(
        idToken,
        "GET",
        `/documents/${documentId}/versions`,
      ),

    // --- Phase 5: Admin panel ---

    listOrganizations: () =>
      request<{ organizations: import("../types").OrganizationRow[] }>(
        idToken,
        "GET",
        "/organizations",
      ),

    createOrganization: (body: {
      name: string;
      orgType: import("../types").OrganizationRow["org_type"];
      npi?: string;
    }) =>
      request<{ organization: import("../types").OrganizationRow }>(
        idToken,
        "POST",
        "/organizations",
        body,
      ),

    /** Admin-creates a Doctor/Nurse/Admin account (invite-email flow,
     * unchanged from before Phase 5) — name/npi/organizationId are
     * required by the backend when role is Doctor or Nurse. */
    createUser: (body: {
      email: string;
      role: import("../types").Role;
      name?: string;
      npi?: string;
      organizationId?: string;
      phone?: string;
    }) => request<{ userId: string }>(idToken, "POST", "/admin/users", body),

    listUsers: () =>
      request<{ users: import("../types").AdminUserSummary[] }>(idToken, "GET", "/admin/users"),

    approveUser: (userId: string, body: { organizationId: string }) =>
      request<{ approved: true }>(idToken, "POST", `/admin/users/${userId}/approve`, body),

    rejectUser: (userId: string, body?: { reason?: string }) =>
      request<{ rejected: true }>(idToken, "POST", `/admin/users/${userId}/reject`, body),

    deleteUser: (userId: string) =>
      request<{ deleted: true }>(idToken, "DELETE", `/admin/users/${userId}`),
  };
}

/** Uploads directly to S3 using the presigned URL — never touches the API
 * Lambda, matching the backend's presigned-PUT design. */
export async function uploadToPresignedUrl(
  uploadUrl: string,
  file: File,
): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!res.ok) {
    throw new ApiError(res.status, `Upload failed: ${res.statusText}`);
  }
}

export async function getPublicSealVerification(
  sealToken: string,
): Promise<import("../types").PublicSealVerification> {
  const res = await fetch(`${API_BASE}/verify/${encodeURIComponent(sealToken)}`);
  return (await parseJsonOrThrow(res)) as import("../types").PublicSealVerification;
}

export async function resolveVerifiedDocument(
  idToken: string,
  sealToken: string,
): Promise<{ documentId: string }> {
  return request<{ documentId: string }>(
    idToken,
    "GET",
    `/verify/${encodeURIComponent(sealToken)}/document`,
  );
}
